import { DebugLogger } from '@affine/debug';
import type {
  WorkspaceNavigationDescriptor,
  WorkspaceViewScope,
} from '@streamlain/copilot-realtime-contract';
import { LiveData } from '@toeverything/infra';
// @ts-expect-error upstream type is wrong
import { createKeybindingsHandler } from 'tinykeys';

import type { AffineCommand, AffineCommandOptions } from './command';
import { createAffineCommand } from './command';

const commandLogger = new DebugLogger('command:registry');
// Derived membership only; command objects/handlers remain owned by the registry.
type NavigationCapability = NonNullable<
  WorkspaceNavigationDescriptor['capability']
>;
interface NavigationMembership {
  commands: Map<string, AffineCommand>;
  capabilities: Map<NavigationCapability, Set<AffineCommand>>;
}
const navigationByScope = new Map<string, NavigationMembership>();
const navigationScopeKey = (scope: WorkspaceViewScope) =>
  JSON.stringify([scope.workspaceId, scope.viewId]);

interface KeyBindingMap {
  [keybinding: string]: (event: KeyboardEvent) => void;
}

export interface KeyBindingOptions {
  /**
   * Key presses will listen to this event (default: "keydown").
   */
  event?: 'keydown' | 'keyup';

  /**
   * Whether to capture the event during the capture phase (default: false).
   */
  capture?: boolean;

  /**
   * Keybinding sequences will wait this long between key presses before
   * cancelling (default: 1000).
   *
   * **Note:** Setting this value too low (i.e. `300`) will be too fast for many
   * of your users.
   */
  timeout?: number;
}

const bindKeys = (
  target: Window | HTMLElement,
  keyBindingMap: KeyBindingMap,
  options: KeyBindingOptions = {}
) => {
  const event = options.event ?? 'keydown';
  const onKeyEvent = createKeybindingsHandler(keyBindingMap, options);
  target.addEventListener(event, onKeyEvent, options.capture);
  return () => {
    target.removeEventListener(event, onKeyEvent, options.capture);
  };
};

class CommandRegistry {
  readonly commands: Map<string, AffineCommand> = new Map();
  readonly changes$ = new LiveData(0);
  private batchDepth = 0;
  private batchChanged = false;
  /** Publish one synchronous owner lifecycle, including nested cleanup, once. */
  batch<T>(operation: () => T): T {
    this.batchDepth++;
    try {
      return operation();
    } finally {
      this.batchDepth--;
      if (!this.batchDepth && this.batchChanged) {
        this.batchChanged = false;
        this.invalidate();
      }
    }
  }
  /** Publish a change to existing owner-provided command guards. */
  invalidate() {
    if (this.batchDepth) {
      this.batchChanged = true;
      return;
    }
    this.changes$.next(this.changes$.value + 1);
  }

  register(options: AffineCommandOptions) {
    if (this.commands.has(options.id)) {
      commandLogger.warn(`Command ${options.id} already registered.`);
      return () => {};
    }
    const command = createAffineCommand(options);
    this.commands.set(command.id, command);
    const navigationKey = command.navigation
      ? navigationScopeKey(command.navigation.scope)
      : null;
    const capability = command.navigation?.definition.capability;
    if (navigationKey !== null) {
      const membership = navigationByScope.get(navigationKey) ?? {
        commands: new Map<string, AffineCommand>(),
        capabilities: new Map<NavigationCapability, Set<AffineCommand>>(),
      };
      membership.commands.set(command.id, command);
      if (capability) {
        const entries =
          membership.capabilities.get(capability) ?? new Set<AffineCommand>();
        entries.add(command);
        membership.capabilities.set(capability, entries);
      }
      navigationByScope.set(navigationKey, membership);
    }

    let unsubKb: (() => void) | undefined;

    if (
      command.keyBinding &&
      !command.keyBinding.skipRegister &&
      typeof window !== 'undefined'
    ) {
      const {
        binding: keybinding,
        capture,
        admit,
        exclusive,
      } = command.keyBinding;
      unsubKb = bindKeys(
        window,
        {
          [keybinding]: (e: KeyboardEvent) => {
            if (admit && !admit(e)) return;
            e.preventDefault();
            if (exclusive) e.stopPropagation();
            command.run()?.catch(e => {
              console.error(`Failed to run command [${command.id}]`, e);
            });
          },
        },
        {
          capture,
        }
      );
    }

    commandLogger.debug(`Registered command ${command.id}`);
    this.invalidate();
    return () => {
      // An old cleanup must not remove a later registration with the same id.
      if (this.commands.get(command.id) !== command) return;
      unsubKb?.();
      this.commands.delete(command.id);
      if (navigationKey !== null) {
        const membership = navigationByScope.get(navigationKey);
        membership?.commands.delete(command.id);
        if (capability) {
          const entries = membership?.capabilities.get(capability);
          entries?.delete(command);
          if (!entries?.size) membership?.capabilities.delete(capability);
        }
        if (!membership?.commands.size) navigationByScope.delete(navigationKey);
      }

      this.invalidate();
      commandLogger.debug(`Unregistered command ${command.id}`);
    };
  }

  get(id: string): AffineCommand | undefined {
    if (!this.commands.has(id)) {
      commandLogger.warn(`Command ${id} not registered.`);
      return undefined;
    }
    return this.commands.get(id);
  }

  /** Native scoped cursor; never materializes unrelated User commands. */
  *iterateNavigationCommands(
    scope: WorkspaceViewScope,
    capabilityFirst?: NavigationCapability
  ): IterableIterator<AffineCommand> {
    const membership = navigationByScope.get(navigationScopeKey(scope));
    if (!membership) return;
    const priority = capabilityFirst
      ? membership.capabilities.get(capabilityFirst)
      : undefined;
    if (priority) {
      for (const command of priority) {
        if (this.commands.get(command.id) === command) yield command;
      }
    }
    for (const command of membership.commands.values()) {
      if (!priority?.has(command) && this.commands.get(command.id) === command)
        yield command;
    }
  }

  getAll(): AffineCommand[] {
    return Array.from(this.commands.values());
  }
}

export const AffineCommandRegistry = new CommandRegistry();

export function registerAffineCommand(options: AffineCommandOptions) {
  return AffineCommandRegistry.register(options);
}
